import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Grid,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { useDispatch } from "react-redux";

import { OAuth as OAuthActions } from "../../js/actions";
import { Context } from "../../js/utils/Context";
import { setSnackbar } from "../../js/reducers/snackbar";
import { setAlertDialog } from "../../js/reducers/alertDialog";

const NAME_REGEX = /^[a-zA-Z0-9_\s]+$/;
const CODE_REGEX = /^[a-zA-Z0-9]+$/;

const ACCENT = "#2563eb";
const ACCENT_DARK = "#1d4ed8";
const TEXT_TITLE = "#0f172a";
const TEXT_PLACEHOLDER = "#94a3b8";
const FIELD_BG = "#f1f5f9";
const ERROR = "#dc2626";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 12,
  },
  title: {
    padding: theme.spacing(2, 2.5),
    fontFamily: theme.typography.fontFamily,
    background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`,
    color: "white",
    "& h2": {
      fontFamily: theme.typography.fontFamily,
      fontWeight: theme.typography.fontWeightSemiBold,
      fontSize: "1rem !important",
      color: "white",
      paddingTop: 3,
    },
  },
  content: {
    padding: theme.spacing(2.5),
    paddingBottom: 0,
    fontFamily: theme.typography.fontFamily,
  },
  fieldGrid: {
    width: "100%",
    margin: 0,
    "& > .MuiGrid-item": {
      paddingTop: "0 !important",
      paddingBottom: "0 !important",
    },
    "& > .MuiGrid-item:nth-last-child(-n+1)": {
      marginBottom: 0,
    },
  },
  fieldGridItem: {
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    paddingLeft: "0 !important",
    paddingRight: "0 !important",
    marginBottom: theme.spacing(1.25),
  },
  fieldGridItemLeft: {
    paddingRight: `${theme.spacing(1)}px !important`,
  },
  fieldGridItemRight: {
    paddingLeft: `${theme.spacing(1)}px !important`,
  },
  fieldLabel: {
    color: TEXT_TITLE,
    fontFamily: theme.typography.fontFamily,
    fontSize: "0.85rem !important",
    fontWeight: theme.typography.fontWeightMedium,
    margin: 0,
    marginBottom: 2,
    padding: 0,
    display: "flex",
    alignItems: "center",
    gap: 4,
    lineHeight: 1.1,
  },
  required: {
    color: ERROR,
    fontWeight: theme.typography.fontWeightSemiBold,
  },
  field: {
    margin: "0 !important",
    "&.MuiFormControl-root": {
      marginTop: "0 !important",
    },
    "& .MuiFilledInput-root": {
      borderRadius: 10,
      backgroundColor: FIELD_BG,
      border: "1px solid transparent",
      fontFamily: theme.typography.fontFamily,
      padding: "0 !important",
      transition:
        "background-color 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
      "&:hover": {
        backgroundColor: "#e9eef5",
      },
      "&.Mui-focused": {
        backgroundColor: "white",
        borderColor: ACCENT,
        boxShadow: `0 0 0 3px rgba(37, 99, 235, 0.15)`,
      },
      "&.Mui-error": {
        borderColor: ERROR,
      },
      "&.Mui-error.Mui-focused": {
        boxShadow: `0 0 0 3px rgba(220, 38, 38, 0.12)`,
      },
      "&::before, &::after": {
        display: "none",
        content: "none",
        border: "none",
      },
    },
    "& .MuiFilledInput-input, & .MuiInputBase-input, & .MuiInputBase-inputHiddenLabel": {
      padding: "14px 12px !important",
      fontFamily: theme.typography.fontFamily,
      fontSize: "0.85rem !important",
      fontWeight: theme.typography.fontWeightRegular,
      color: TEXT_TITLE,
      "&::placeholder": {
        color: TEXT_PLACEHOLDER,
        fontFamily: theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeightRegular,
        opacity: 1,
      },
    },
    "& .MuiFilledInput-input.MuiSelect-select, & .MuiSelect-select.MuiSelect-selectMenu, & .MuiSelect-root.MuiInputBase-input": {
      padding: "12px 32px 12px 12px !important",
      minHeight: "20px !important",
      height: "20px !important",
      lineHeight: "20px !important",
      boxSizing: "content-box !important",
      display: "block",
    },
    "& .MuiSelect-icon": {
      color: "#64748b",
      right: 8,
      top: "calc(50% - 12px)",
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 2,
      marginTop: 2,
      fontFamily: theme.typography.fontFamily,
      fontSize: "0.7rem !important",
      fontWeight: theme.typography.fontWeightRegular,
    },
  },
  selectMenuPaper: {
    marginTop: 4,
    borderRadius: 8,
    boxShadow: "0 4px 16px rgba(15, 23, 42, 0.12)",
    maxHeight: 240,
    "& .MuiMenuItem-root": {
      fontFamily: theme.typography.fontFamily,
      fontSize: "0.82rem !important",
      minHeight: "auto",
      padding: theme.spacing(0.75, 1.5),
    },
    "& .MuiList-padding": {
      paddingTop: 4,
      paddingBottom: 4,
    },
  },
  textarea: {
    "& .MuiFilledInput-input, & .MuiInputBase-input": {
      padding: "10px 12px !important",
      minHeight: 56,
    },
  },
  actions: {
    padding: theme.spacing(1, 2.5, 1.5, 2.5),
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    "& > :not(:first-child)": {
      marginLeft: 0,
    },
  },
  primaryBtn: {
    textTransform: "none",
    backgroundColor: "#1d4ed8",
    color: "#fff",
    borderRadius: 8,
    padding: "6px 16px",
    minWidth: 84,
    fontSize: "0.85rem !important",
    "&:hover": {
      backgroundColor: "#1e40af",
    },
    "&.Mui-disabled": {
      backgroundColor: "#e2e8f0",
      color: "#94a3b8",
      border: "1px solid #cbd5e1",
    },
  },
  cancelBtn: {
    color: TEXT_TITLE,
    backgroundColor: "white",
    textTransform: "none",
    fontFamily: theme.typography.fontFamily,
    fontSize: "0.85rem !important",
    fontWeight: theme.typography.fontWeightSemiBold,
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    padding: "6px 16px",
    minWidth: 84,
    "&:hover": {
      backgroundColor: "#f8fafc",
      border: "1px solid #94a3b8",
    },
  },
}));

const initialState = {
  plantName: "",
  plantCode: "",
  address: "",
  country: "",
  state: "",
  city: "",
};

const initialErrors = {
  plantName: "",
  plantCode: "",
};

const AddPlantDialog = ({ open, onClose, onAdded }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [makeSelected] = useContext(Context);

  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState(initialErrors);
  const [submitting, setSubmitting] = useState(false);
  const [countryData, setCountryData] = useState([]);
  const [stateData, setStateData] = useState([]);
  const [cityData, setCityData] = useState([]);

  useEffect(() => {
    if (!open) {
      setValues(initialState);
      setErrors(initialErrors);
      setSubmitting(false);
      setStateData([]);
      setCityData([]);
      return;
    }
    dispatch(OAuthActions.getCountry()).then((data) => {
      if (data && data.country) setCountryData(data.country);
    });
  }, [open, dispatch]);

  const validateField = (field, value) => {
    if (!value) return "";
    switch (field) {
      case "plantName":
        return NAME_REGEX.test(value)
          ? ""
          : "Plant Name must contain only alphabets, digits and underscore";
      case "plantCode":
        return CODE_REGEX.test(value)
          ? ""
          : "Plant Code must contain only alphabets and digits";
      default:
        return "";
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
    if (field in errors) {
      setErrors((prev) => ({
        ...prev,
        [field]: validateField(field, value),
      }));
    }
  };

  const handleCountryChange = (e) => {
    const countryUid = e.target.value;
    setValues((prev) => ({
      ...prev,
      country: countryUid,
      state: "",
      city: "",
    }));
    setStateData([]);
    setCityData([]);
    if (countryUid) {
      dispatch(OAuthActions.getState(countryUid)).then((data) => {
        if (data && data.state) setStateData(data.state);
      });
    }
  };

  const handleStateChange = (e) => {
    const stateUid = e.target.value;
    setValues((prev) => ({ ...prev, state: stateUid, city: "" }));
    setCityData([]);
    if (stateUid) {
      dispatch(OAuthActions.getCity(stateUid)).then((data) => {
        if (data && data.city) setCityData(data.city);
      });
    }
  };

  const handleCityChange = (e) => {
    setValues((prev) => ({ ...prev, city: e.target.value }));
  };

  const requiredFilled = values.plantName.trim() !== "";
  const noErrors = Object.values(errors).every((e) => e === "");
  const canSubmit = requiredFilled && noErrors && !submitting;

  const handleSubmit = async () => {
    if (!makeSelected || !makeSelected.uid) {
      dispatch(
        setSnackbar(
          true,
          "info",
          "Please select an OEM on the dashboard before adding a plant."
        )
      );
      return;
    }
    setSubmitting(true);
    try {
      const success = await dispatch(
        OAuthActions.createPlant(
          values.plantCode,
          values.plantName,
          values.address,
          values.country,
          values.state,
          values.city,
          makeSelected.uid
        )
      );
      if (success && success.error === undefined) {
        dispatch(setSnackbar(true, "success", "Add Plant Successful"));
        if (onAdded) onAdded();
        onClose();
      } else {
        dispatch(
          setAlertDialog(
            true,
            null,
            "error",
            "Add Plant",
            "Add plant unsuccessful.\nDetails of failure: " +
            (success && success.message ? success.message : "Unknown error"),
            "OK",
            ""
          )
        );
      }
    } catch (err) {
      dispatch(
        setSnackbar(
          true,
          "error",
          "Unable to add plant. Please try again."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogTitle disableTypography className={classes.title}>
        <Typography variant="h6" component="h2" style={{ fontWeight: 700 }}>
          Add Plant
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Grid container spacing={2} className={classes.fieldGrid}>
          <Grid
            item
            xs={12}
            sm={6}
            className={`${classes.fieldGridItem} ${classes.fieldGridItemLeft}`}
          >
            <div className={classes.fieldLabel}>
              Plant Name <span className={classes.required}>*</span>
            </div>
            <TextField
              fullWidth
              variant="filled"
              hiddenLabel
              value={values.plantName}
              onChange={handleChange("plantName")}
              error={errors.plantName.length !== 0}
              helperText={errors.plantName}
              className={classes.field}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            className={`${classes.fieldGridItem} ${classes.fieldGridItemRight}`}
          >
            <div className={classes.fieldLabel}>Plant Code</div>
            <TextField
              fullWidth
              variant="filled"
              hiddenLabel
              value={values.plantCode}
              onChange={handleChange("plantCode")}
              error={errors.plantCode.length !== 0}
              helperText={errors.plantCode}
              className={classes.field}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            className={`${classes.fieldGridItem} ${classes.fieldGridItemLeft}`}
          >
            <div className={classes.fieldLabel}>Country</div>
            <TextField
              fullWidth
              select
              variant="filled"
              hiddenLabel
              value={values.country}
              onChange={handleCountryChange}
              className={classes.field}
              InputProps={{ disableUnderline: true }}
              SelectProps={{
                displayEmpty: true,
                MenuProps: {
                  classes: { paper: classes.selectMenuPaper },
                  getContentAnchorEl: null,
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                },
              }}
            >
              {countryData.map((c) => (
                <MenuItem key={c.uid} value={c.uid}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            className={`${classes.fieldGridItem} ${classes.fieldGridItemRight}`}
          >
            <div className={classes.fieldLabel}>State</div>
            <TextField
              fullWidth
              select
              variant="filled"
              hiddenLabel
              value={values.state}
              onChange={handleStateChange}
              disabled={!values.country}
              className={classes.field}
              InputProps={{ disableUnderline: true }}
              SelectProps={{
                displayEmpty: true,
                MenuProps: {
                  classes: { paper: classes.selectMenuPaper },
                  getContentAnchorEl: null,
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                },
              }}
            >
              {stateData.map((s) => (
                <MenuItem key={s.uid} value={s.uid}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            className={`${classes.fieldGridItem} ${classes.fieldGridItemLeft}`}
          >
            <div className={classes.fieldLabel}>City</div>
            <TextField
              fullWidth
              select
              variant="filled"
              hiddenLabel
              value={values.city}
              onChange={handleCityChange}
              disabled={!values.state}
              className={classes.field}
              InputProps={{ disableUnderline: true }}
              SelectProps={{
                displayEmpty: true,
                MenuProps: {
                  classes: { paper: classes.selectMenuPaper },
                  getContentAnchorEl: null,
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                },
              }}
            >
              {cityData.map((ci) => (
                <MenuItem key={ci.uid} value={ci.uid}>
                  {ci.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} className={classes.fieldGridItem}>
            <div className={classes.fieldLabel}>Address</div>
            <TextField
              fullWidth
              variant="filled"
              hiddenLabel
              value={values.address}
              onChange={handleChange("address")}
              multiline
              rows={2}
              className={`${classes.field} ${classes.textarea}`}
              InputProps={{ disableUnderline: true }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          className={classes.cancelBtn}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          className={classes.primaryBtn}
          variant="contained"
          disableElevation
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={
            submitting ? (
              <CircularProgress size={16} style={{ color: "white" }} />
            ) : null
          }
        >
          {submitting ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPlantDialog;
